import { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, money } from "@/lib/api";

const Stat = ({ label, value, testid }) => (
  <div className="bg-[#FDFBF7] p-6" data-testid={testid}>
    <p className="field-label">{label}</p>
    <p className="mt-3 font-display text-4xl font-light">{value}</p>
  </div>
);

const Panel = ({ title, children, className = "" }) => (
  <div className={`bg-[#FDFBF7] p-6 ${className}`}>
    <p className="eyebrow mb-6">{title}</p>
    {children}
  </div>
);

const TopList = ({ rows, testid }) => (
  <ul className="space-y-3 text-sm" data-testid={testid}>
    {rows.length === 0 && <li className="text-[#8A8780]">No data yet</li>}
    {rows.map((r) => (
      <li key={r.name} className="flex justify-between gap-4 border-b border-[#F0EDE6] pb-2">
        <span className="truncate">{r.name}</span>
        <span className="text-[#5A626A]">{r.count}</span>
      </li>
    ))}
  </ul>
);

const tick = { fontSize: 11, fill: "#8A8780" };
const short = (d) => d.slice(5);

export const OverviewTab = () => {
  const [t, setT] = useState(null);
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setT(r.data)).catch(() => {});
    api.get("/admin/sales").then((r) => setS(r.data)).catch(() => {});
  }, []);

  if (!t || !s) return <div className="h-64 animate-pulse bg-[#FDFBF7]" />;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Revenue (paid)" value={money(s.revenue)} testid="stat-revenue" />
        <Stat label="Orders" value={s.orders} testid="stat-orders" />
        <Stat label="Avg. order" value={money(s.avgOrderValue)} testid="stat-aov" />
        <Stat label="Page views · 30d" value={t.pageViews} testid="stat-pageviews" />
        <Stat label="Unique visitors · 30d" value={t.uniqueVisitors} testid="stat-visitors" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Traffic · last 30 days" className="lg:col-span-2">
          <div className="h-64" data-testid="traffic-chart">
            <ResponsiveContainer>
              <AreaChart data={t.daily}>
                <CartesianGrid stroke="#F0EDE6" vertical={false} />
                <XAxis dataKey="date" tickFormatter={short} tick={tick} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={tick} axisLine={false} tickLine={false} width={30} />
                <Tooltip />
                <Area type="monotone" dataKey="views" stroke="#1A1A1A" fill="#E8E3D9" strokeWidth={1.5} name="Page views" />
                <Area type="monotone" dataKey="visitors" stroke="#8C7A5B" fill="transparent" strokeWidth={1.5} name="Visitors" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Top referrers"><TopList rows={t.referrers} testid="top-referrers" /></Panel>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Revenue · last 30 days (AUD)" className="lg:col-span-2">
          <div className="h-56" data-testid="revenue-chart">
            <ResponsiveContainer>
              <BarChart data={s.daily}>
                <CartesianGrid stroke="#F0EDE6" vertical={false} />
                <XAxis dataKey="date" tickFormatter={short} tick={tick} axisLine={false} tickLine={false} />
                <YAxis tick={tick} axisLine={false} tickLine={false} width={40} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="revenue" fill="#1A1A1A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Top pages"><TopList rows={t.pages} testid="top-pages" /></Panel>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Stat label="Originals sold" value={s.originalsSold} testid="stat-originals-sold" />
        <Stat label="Prints sold" value={s.printsSold} testid="stat-prints-sold" />
        <Stat label="Shipping" value={money(s.shippingCollected)} testid="stat-shipping" />
        <Stat label="Insurance" value={money(s.insuranceCollected)} testid="stat-insurance" />
        <Stat label="Discounts" value={money(s.discountsGiven)} testid="stat-discounts" />
        <Stat label="Unpaid checkouts" value={s.pendingCheckouts} testid="stat-pending" />
      </div>
    </div>
  );
};
